"use client";

import { useEffect } from "react";
import { applyStudioCursorTheme } from "@/studio/shell/studioCursorTheme";

export const STUDIO_CUSTOM_CURSOR_KEY = "yatishara-studio-custom-cursor";

export function readStudioCustomCursorEnabled(): boolean {
  if (typeof window === "undefined") return true;
  return window.localStorage.getItem(STUDIO_CUSTOM_CURSOR_KEY) !== "off";
}

const CLICKABLE_SELECTOR =
  'button, a, [role="button"], [role="tab"], [role="menuitem"], [role="option"], [data-clickable], .cursor-tree-row, .desk-file-list-row, .desk-file-grid-item, .desk-file-preview-item, .cursor-unified-tab, .studio-inline-tag, .cursor-tab-close, .cursor-clickable, [class*="cursor-tab"], [class*="cursor-tree"], .studio-admin-head-tab, .studio-cn-head-tab, [role="tablist"] button, .studio-auth-primary, .studio-auth-link';

function isClickableTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(CLICKABLE_SELECTOR));
}

function isTouchPointer(event: PointerEvent): boolean {
  return event.pointerType === "touch";
}

/** Accent SVG cursors + pointer hover class on :root (desktop only). */
export function useStudioCustomCursor(enabled: boolean, themeAnchor?: HTMLElement | null) {
  useEffect(() => {
    if (!enabled) return;

    const updateCursor = () => applyStudioCursorTheme(themeAnchor ?? document.documentElement);
    updateCursor();

    const observer = new MutationObserver((mutations) => {
      if (
        mutations.some(
          (mutation) =>
            mutation.attributeName === "data-theme" ||
            mutation.attributeName === "data-appearance" ||
            mutation.attributeName === "style",
        )
      ) {
        updateCursor();
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "data-appearance", "style"],
    });
    window.addEventListener("mercuryos-theme-change", updateCursor);

    const root = document.documentElement;

    const handlePointerOver = (event: PointerEvent) => {
      if (isTouchPointer(event)) return;
      if (isClickableTarget(event.target)) {
        root.classList.add("is-cursor-interactive");
      }
    };
    const handlePointerOut = (event: PointerEvent) => {
      if (isTouchPointer(event)) return;
      if (!isClickableTarget(event.relatedTarget)) {
        root.classList.remove("is-cursor-interactive");
      }
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (isTouchPointer(event)) return;
      if (isClickableTarget(event.target)) {
        root.classList.add("is-cursor-interactive");
      }
    };
    const handlePointerUp = (event: PointerEvent) => {
      if (isTouchPointer(event)) return;
      const atPoint = document.elementFromPoint(event.clientX, event.clientY);
      if (!isClickableTarget(atPoint)) {
        root.classList.remove("is-cursor-interactive");
      }
    };

    document.addEventListener("pointerover", handlePointerOver, true);
    document.addEventListener("pointerout", handlePointerOut, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("pointerup", handlePointerUp, true);
    document.addEventListener("pointercancel", handlePointerUp, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("mercuryos-theme-change", updateCursor);
      document.removeEventListener("pointerover", handlePointerOver, true);
      document.removeEventListener("pointerout", handlePointerOut, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("pointerup", handlePointerUp, true);
      document.removeEventListener("pointercancel", handlePointerUp, true);
      root.classList.remove("is-cursor-interactive");
    };
  }, [enabled]);
}
